import { Address } from '@thirdweb-dev/sdk';
import { create } from 'zustand';
import { Currency } from '@cryptoalgebra/kim-sdk';
import { Token } from '@app/types/token';
import { useBalance, useContract } from '@thirdweb-dev/react';
import { useIsDepositTokenApproved } from '@app/hooks/ichi/useIsDepositTokenApproved';
import { useAsyncFn } from 'react-use';
import { EMPTY_ADDRESS } from '@app/constants/common';
import { useConfig } from '@app/config';
import { useEffect, useMemo, useRef } from 'react';
import { useIsNitroPool } from '@app/hooks/nitro/useIsNitroPool';
import { useAlgebraPoolToken0 } from '@app/hooks/v3/useAlgebraPoolToken0';
import { useRouter } from 'next/router';
import { useIchiVaultsList } from '@app/hooks/ichi/useIchiVaultsList';
import { Vault } from '@app/hooks/plugins/useVaults';
import {
  AlgebraPoolNitroDetails,
  useAlgebraPoolNitroDetails
} from '@app/hooks/nitro/useAlgebraPoolNitroDetails';

interface LiquidityByIchiState {
  readonly vaultId: Address | null;
  readonly amount: string;
  readonly lockDuration: number;
  readonly stakeToNitro: boolean;
  readonly actions: {
    setVaultId: (vaultId: Address) => void;
    setAmount: (amount: string) => void;
    setLockDuration: (val: number) => void;
    setStakeToNitro: (val: boolean) => void;
  };
}

export interface VaultDetails {
  vaultId: string;
  depositToken: Token;
  pairedToken: Token;
  apr: number | undefined | null;
  tvl: string | undefined | null;
}

const initialState = {
  vaultId: null,
  amount: '',
  lockDuration: 0,
  stakeToNitro: false
};

export const useLiquidityByIchiState = create<LiquidityByIchiState>(set => ({
  ...initialState,
  actions: {
    setVaultId: vaultId => set({ vaultId }),
    setAmount: amount => set({ amount }),
    setLockDuration: (val: number) => set({ lockDuration: val }),
    setStakeToNitro: (val: boolean) => set({ stakeToNitro: val })
  }
}));

export interface IDerivedLiquidityByIchiInfo {
  vault: Vault | null;
  amount: string;
  // vaultMetrics: VaultMetrics[] | null | undefined;
  // totalSupply: string | null | undefined;
  vaultDetails: VaultDetails | null;
  isApproved: boolean | undefined;
  nftPoolAddress: string | null | undefined;
  getPoolAddress: () => Promise<string | null | undefined>;
  isSwappedPair: boolean;
  lockDuration: number;
  depositTokenBalance: string | undefined;
  isBalanceExceeded: boolean;
  isNitroPool: boolean;
  nitroDetails: AlgebraPoolNitroDetails | null;
}

export function useDerivedLiquidityByIchiInfo(
  poolAddress: Address | undefined
): IDerivedLiquidityByIchiInfo {
  const { query } = useRouter();
  const config = useConfig();
  const {
    vaultId,
    amount,
    lockDuration,
    actions: { setVaultId }
  } = useLiquidityByIchiState();

  const { data: ichiVaults, isLoading } = useIchiVaultsList();

  const vault = useMemo(() => {
    return ichiVaults?.find(
      item => item.id?.toLowerCase() === vaultId?.toLowerCase()
    );
  }, [ichiVaults, vaultId]);

  const { data: poolToken0 } = useAlgebraPoolToken0({ address: poolAddress });

  const { data: depositTokenBalance } = useBalance(vault?.token0.id ?? '');

  const { data: isApproved } = useIsDepositTokenApproved(amount, vaultId);

  const isBalanceExceeded = depositTokenBalance
    ? +depositTokenBalance?.displayValue < +amount
    : false;

  const { contract: poolFactoryContract } = useContract(
    config?.CONTRACTS.NFT_POOL_FACTORY
  );

  const [{ value: nftPoolAddress }, getPoolAddress] = useAsyncFn(async () => {
    if (!poolFactoryContract || !vaultId) {
      return;
    }

    const nftPoolAddress = await poolFactoryContract.call('getPool', [vaultId]);

    return nftPoolAddress === EMPTY_ADDRESS ? null : nftPoolAddress;
  }, [poolFactoryContract, vaultId]);

  const nitroDetails = useAlgebraPoolNitroDetails(nftPoolAddress);

  const { isNitroPool } = useIsNitroPool(nftPoolAddress);

  useEffect(() => {
    getPoolAddress();
  }, [getPoolAddress]);

  const vaultSetRef = useRef(false);

  useEffect(() => {
    if (query && query.vaultId && !vaultSetRef.current) {
      const { vaultId } = query;

      vaultSetRef.current = true;

      setVaultId(vaultId as string);
    }
  }, [query.vaultId, query, setVaultId]);

  const isSwappedPair =
    poolToken0?.toLowerCase() !== vault?.token0.id.toLowerCase();

  const vaultDetails: VaultDetails | null = vault
    ? {
        vaultId: vault.id ?? '',
        depositToken: {
          symbol: vault.token0.symbol,
          contractAddress: vault.token0.id,
          decimals: +vault.token0.decimals,
          name: vault.token0.name,
          logoUri: ''
        },
        pairedToken: {
          symbol: vault.token1.symbol,
          contractAddress: vault.token1.id,
          decimals: +vault.token1.decimals,
          name: vault.token1.name,
          logoUri: ''
        },
        apr: vault.apr,
        tvl: vault.tvl?.toString() ?? ''
      }
    : null;

  return {
    amount,
    // vaultMetrics,
    // totalSupply,
    vault: vault ?? null,
    vaultDetails,
    isApproved,
    nftPoolAddress,
    getPoolAddress,
    isSwappedPair,
    lockDuration,
    depositTokenBalance: depositTokenBalance?.displayValue,
    isBalanceExceeded,
    isNitroPool,
    nitroDetails
  };
}
